import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Save,
  CheckCircle,
  Loader2
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useBooking, DAYS_OF_WEEK } from "../../context/BookingContext";

export default function ExpertSchedulePage() {
  const { user } = useAuth();
  const {
    getSchedule,
    setSchedule,
    getBookingsForExpert,
    schedulesLoaded
  } = useBooking();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const expertId = user?.id || "expert-1";
  const existingSchedule = getSchedule(expertId);
  const bookings = getBookingsForExpert(expertId);

  const [schedule, setLocalSchedule] = useState(() => {
    return DAYS_OF_WEEK.map((day) => {
      const existing = existingSchedule.find((s) => s.dayId === day.id);
      return (
        existing || {
          dayId: day.id,
          label: day.label,
          enabled: day.id < 5,
          startHour: 9,
          endHour: 18
        }
      );
    });
  });

  useEffect(() => {
    if (schedulesLoaded && existingSchedule.length > 0) {
      setLocalSchedule(
        DAYS_OF_WEEK.map((day) => {
          const existing = existingSchedule.find((s) => s.dayId === day.id);
          return (
            existing || {
              dayId: day.id,
              label: day.label,
              enabled: day.id < 5,
              startHour: 9,
              endHour: 18
            }
          );
        })
      );
    }
  }, [schedulesLoaded, existingSchedule.length]);

  const toggleDay = (dayId) => {
    setLocalSchedule((prev) =>
      prev.map((s) =>
        s.dayId === dayId ? { ...s, enabled: !s.enabled } : s
      )
    );
  };

  const updateHour = (dayId, field, value) => {
    setLocalSchedule((prev) =>
      prev.map((s) =>
        s.dayId === dayId ? { ...s, [field]: Number(value) } : s
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    await setSchedule(expertId, schedule);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <Layout hideFooter>
      <div id="expert-schedule_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/expert/dashboard"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-lg font-extralight">Назад в кабинет</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-medium text-white mb-2">
                  Расписание консультаций
                </h1>
                <p className="text-lg font-extralight text-neutral-400">
                  Укажите дни и часы, когда вы готовы проводить консультации
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-400" />
                <span className="text-lg font-extralight text-neutral-400">
                  {bookings.length} активных бронирований
                </span>
              </div>
            </div>

            {!schedulesLoaded ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
                <span className="ml-3 text-lg font-extralight text-neutral-400">
                  Загрузка расписания...
                </span>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-8">
                  {schedule.map((day) => (
                    <div
                      key={day.dayId}
                      className={`p-5 rounded-md border transition-colors ${
                        day.enabled
                          ? "bg-neutral-900/50 border-neutral-800"
                          : "bg-neutral-950/50 border-neutral-800/30"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleDay(day.dayId)}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            day.enabled ? "bg-primary-600" : "bg-neutral-700"
                          }`}
                        >
                          <div
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              day.enabled ? "left-6" : "left-1"
                            }`}
                          />
                        </button>

                        <span
                          className={`text-lg font-medium w-32 ${
                            day.enabled ? "text-white" : "text-neutral-600"
                          }`}
                        >
                          {DAYS_OF_WEEK.find((d) => d.id === day.dayId)?.label}
                        </span>

                        {day.enabled && (
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-neutral-500" />
                              <span className="text-lg font-extralight text-neutral-400">
                                с
                              </span>
                              <select
                                value={day.startHour}
                                onChange={(e) =>
                                  updateHour(day.dayId, "startHour", e.target.value)
                                }
                                className="px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                              >
                                {hours.map((h) => (
                                  <option key={h} value={h}>
                                    {String(h).padStart(2, "0")}:00
                                  </option>
                                ))}
                              </select>
                              <span className="text-lg font-extralight text-neutral-400">
                                до
                              </span>
                              <select
                                value={day.endHour}
                                onChange={(e) =>
                                  updateHour(day.dayId, "endHour", e.target.value)
                                }
                                className="px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                              >
                                {hours
                                  .filter((h) => h > day.startHour)
                                  .map((h) => (
                                    <option key={h} value={h}>
                                      {String(h).padStart(2, "0")}:00
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <span className="text-lg font-extralight text-neutral-500 ml-auto">
                              {day.endHour - day.startHour} ч
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    {saved && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-2 text-accent-400"
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-lg font-extralight">
                          Расписание сохранено в базу данных
                        </span>
                      </motion.div>
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-8 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
                  >
                    {saving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    {saving ? "Сохранение..." : "Сохранить"}
                  </button>
                </div>
              </>
            )}

            {bookings.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-medium text-white mb-6">
                  Забронированные слоты
                </h2>
                <div className="space-y-3">
                  {bookings
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-center gap-4 p-4 rounded-md bg-neutral-900/50 border border-neutral-800"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <span className="text-primary-400 font-medium">
                            {booking.clientName?.[0] || "K"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-lg font-medium text-white">
                            {booking.clientName || "Клиент"}
                          </p>
                          <p className="text-lg font-extralight text-neutral-400">
                            {booking.topic || "Без темы"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-medium text-white">
                            {new Date(booking.date).toLocaleDateString("ru-RU")}
                          </p>
                          <p className="text-lg font-extralight text-primary-400">
                            {booking.time}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
